import {
  type EnterpriseThemeContract,
  KindredConfigProvider,
  KindredHead,
  type KindredConfig,
} from "@bestowinc/kindred-ds";
import { useMemo } from "@bestowinc/tinkertoy-shell-deps/react";
import type { ReactNode } from "@bestowinc/tinkertoy-shell-deps/react";
import { ThemeProvider } from "@bestowinc/tinkertoy-shell-deps/styled-components";
import { createPortal } from "react-dom";

import { ShellGlobalStyle } from "./ShellGlobalStyle";
import type { DeveloperToolsConfig } from "./developer-tools";
import { LoadingSlotProvider } from "./loading/loading-slot-context";
import type { LoadingSlot } from "./loading/loading-slot";
import type { ModuleLoader } from "./module-loader";
import { ModuleLoaderProvider } from "./module-loader/module-loader-context";
import { ShellRuntimeProvider } from "./shell-runtime";
import type { ShellRuntimeConfig } from "./shell-runtime";
import type { ViewLoader } from "./view-loader";
import { ViewLoaderProvider } from "./view-loader";

export interface InternalTinkertoyProviderProps {
  /** Styled-components theme for Kindred + view modules. */
  theme: EnterpriseThemeContract;
  /** CDN module loader consumed via {@link useModuleLoader}. */
  moduleLoader: ModuleLoader;
  /**
   * CDN view loader consumed via {@link useViewLoader}, used by
   * `TinkertoyShell` to import the matched view bundle. Optional: only the
   * catch-all view route needs it, so error-boundary-only test trees (which
   * never mount `TinkertoyShell`'s success path) may omit it.
   */
  viewLoader: ViewLoader;
  /**
   * Base URL for POSTs to the Tinkertoy server `/action` endpoint. Exposed
   * to descendants via {@link useShellRuntime}; constant for the lifetime
   * of the mounted shell.
   */
  apiBaseUrl: string;
  developerTools?: DeveloperToolsConfig;
  /**
   * Carrier Kindred config (tokens + misc) from platform-config. When omitted,
   * the shell skips {@link KindredHead} — matching Remix hosts that skip
   * `<KindredHead>` when platform-config returns no config.
   */
  kindredConfig?: KindredConfig;
  /**
   * The loading slot the shell loader writes streamed loading metadata into.
   * `ShellRoot` passes the one it gave the router; trees without one (memory
   * routers in tests) read a detached slot, so no loading state ever shows.
   */
  loadingSlot?: LoadingSlot;
  children: ReactNode;
}

/**
 * Single place that wires up every cross-cutting provider the Tinkertoy shell
 * owns — theme, module loader, shell runtime config, and (over time) anything
 * else every view needs in scope.
 *
 * Deliberately does **not** render the React Router `<RouterProvider>`. The
 * router is injected by whatever renders us — `ShellRoot` uses the real
 * browser router built by `createShellRouter`; tests (e.g.
 * `ShellErrorBoundary.test.tsx`) use `createMemoryRouter` so they can drive
 * loader failures and navigations directly. Keeping the router on the outside
 * lets both paths share the rest of the provider stack.
 *
 * Not part of the public API — do not export from the package root. Tests
 * import it directly from this module.
 */
export function InternalTinkertoyProvider({
  theme,
  moduleLoader,
  viewLoader,
  apiBaseUrl,
  developerTools,
  kindredConfig,
  loadingSlot,
  children,
}: InternalTinkertoyProviderProps) {
  const runtime = useMemo<ShellRuntimeConfig>(
    () => ({ apiBaseUrl, developerTools }),
    [apiBaseUrl, developerTools],
  );

  // The shell mounts no `<CarrierProvider>`: carrier context is a per-module
  // concern now. Each module bundles its own provider+hook and self-wraps its
  // tree from server data, so the shell can't (and must not) own that context
  return (
    <KindredConfigProvider config={kindredConfig ?? null}>
      <ThemeProvider theme={theme}>
        {/*
         * Portal KindredHead into document.head. The v2 shell mounts under
         * #root (CSR-only); Remix hosts render the same component inline via
         * createHead. createPortal avoids react-helmet's inability to unwrap
         * custom components / fragments.
         */}
        {kindredConfig?.tokens
          ? createPortal(
              <KindredHead tokens={kindredConfig.tokens} />,
              document.head,
            )
          : null}
        <ShellGlobalStyle />
        <ShellRuntimeProvider value={runtime}>
          <ModuleLoaderProvider loader={moduleLoader}>
            <LoadingSlotProvider value={loadingSlot}>
              {viewLoader ? (
                <ViewLoaderProvider loader={viewLoader}>
                  {children}
                </ViewLoaderProvider>
              ) : (
                children
              )}
            </LoadingSlotProvider>
          </ModuleLoaderProvider>
        </ShellRuntimeProvider>
      </ThemeProvider>
    </KindredConfigProvider>
  );
}
